import { useState, useEffect } from 'react';
import moment from 'moment';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DynamicObjectInterface } from '../../../../sdk/type';
import {
  Accordion,
  AdminPageHeader,
  ButtonStyles,
  Card,
  DatePicker,
  DropDown,
  DropDownDataInterface,
  DropDownValidations,
  IconButton,
  IconStyle,
  Input,
  InputTypes,
  InputValidations,
  MLPVersion,
  RadioButton,
  Status,
  Tag,
  TagStyle,
  TextArea,
  Theme,
  Toggle,
  ToggleStyle,
} from '../../../../ui/components';
import { LoggerService } from '../../../../ui/utils';
import { convertUTCdate, convertLocalDate } from '../../../../ui/utils/helper';
import RewardService from '../../../../sdk/services/rewardService';
import PinService from '../../../../sdk/services/pinService';
import CampaignService from '../../../../sdk/services/campaignService';
import { useAdminToast } from '../../../../ui/components/AdminToast/AdminToast';
import { useAPICall } from '../../../../hooks';
import { ControlIconType, FontSize } from '../../../../ui/components/Accordion/Accordion';
import { initialCampaignData } from '../../../../__mockData/initialCampaignData';
import { formatCampaignId } from '../../../../ui/utils/common';
import { DropDownData } from '../../../../ui/components/Form/DropDown/types/propsType';
import { REACT_APP_COUNTRY } from '../../../../envVars';
import styles from './index.module.scss';
import {
  CheckInModal,
  FileUploaderModal,
  OrderRuleModal,
  ruleOperatorList,
  ruleTypeList,
  WinningCriteriaModal,
} from './editModal';

interface Validity {
  type: string;
  value: number;
  bufferDays: number;
  bufferMonths: number;
}

interface Pin {
  pinId: string | number;
  pinName: string;
}

interface PinIdSegment {
  id: string | number;
  name: string;
  list: number[];
}

interface SubKpiCode {
  subKpiCode: string;
  points: string;
  targetFrom: string;
  targetTo: string;
  indicatorName: string;
  indicatorMin?: string;
  indicatorMax?: string;
  indicatorMeasurementUnit?: string;
  indicatorPlaceholder?: string;
  indicatorWinningCriteria?: string;
  indicatorPrerequisiteCriteria?: string;
  indicatorPrerequisite?: string;
  displayInProgressMonitoring?: string;
  disablePreAssessment?: number;
  // Field is shown/editable in pre-assessment, but winning is judged against the
  // post-assessment value only (pre value entered but ignored for scoring).
  ignorePreAssessment?: number;
  // When 1, this indicator MUST pass for the participant to win (on top of the
  // overall winCount). Undefined/0 = optional (legacy behaviour).
  mustHit?: number;
  percentageCalculation?: number;
  genderAgeThresholds?: GenderAgeThresholds;
  // Device this indicator variant belongs to (matches SubChallenge.testDevices[].code).
  // Undefined = device-agnostic (always shown/judged) for backward compatibility.
  testDevice?: string;
}

export interface TestDevice {
  code: string;
  name: string;
}

// Passing standard that varies by gender x age group (e.g. grip strength).
// When present, the indicator is assessed against the participant's absolute
// measured value vs the threshold for their gender/ageGroup (MY only).
type AgeGroupThreshold = Record<string, string>;
interface GenderAgeThresholds {
  MALE?: AgeGroupThreshold;
  FEMALE?: AgeGroupThreshold;
}

interface FileUploader {
  fieldCode: string;
  fieldName: string;
  numberOfFile: string;
  // When 1, this upload is only shown/required in the post-assessment form (not pre).
  postAssessmentOnly?: number;
}

interface Rule {
  actionType: string;
  kpiCode: string;
  description: string;
  calculationType: string;
  targetFrom: string;
  targetTo: string;
  fullfillmentType: string | null;
  point: string;
  reward: string | null;
  voucher?: string;
  subKpiCode: SubKpiCode[] | [];
}

interface RuleList {
  type: string;
  operator?: string;
  value: DynamicObjectInterface;
}

type RuleGroup = RuleList[];

interface Participant {
  name: string;
  status: string;
  description: string;
  teamSize: number;
  enrollmentType: string;
  validity: Validity;
  pin: Pin[];
  rules: Rule[];
  teamLeaderReminderDays?: number | string;
  newAbo?: NewAbo;
  fileUploader?: FileUploader[] | [];
  participationType?: string[];
  canJoinMultipleTeam?: number;
  rewards?: {
    showRewardSelectionForm?: number;
    rewardOptions?: {
      team: string[];
      ind: string[];
    };
  };
}

interface NewAbo {
  isRequired: number | '';
  numOfNewAbo: number | '';
  numOfDays: number | '';
}

interface Order {
  maxOrderNumber: number;
  orderPeriod: number;
  rules: RuleGroup[];
  amount: Amount;
}

interface Amount {
  value: number;
  currency: string;
}

export interface SubChallenge {
  subChallengeId: string;
  name: string;
  description?: string;
  assessmentTitle?: string;
  winCount: number;
  // Per-sub-challenge "Buy Bundles" link shown on the check-eligibility tiles
  // (create-team + join-team). Falls back to REACT_APP_AMWAY when empty.
  buyBundleLink?: string;
  // Per-sub-challenge override of the campaign-level Front/Side Profile Image
  // Section flag. Only consulted in MULTI mode; SINGLE keeps the campaign-level
  // campaignIsProfileImageSectionShown. Resolved via resolveProfileImageSectionShown.
  campaignIsProfileImageSectionShown?: number;
  order: Order;
  rules: Rule[];
  fileUploader?: FileUploader[] | [];
  // Measurement devices for this challenge. When non-empty, each indicator may be
  // tagged (rules[4].subKpiCode[].testDevice) so the participant picks one device.
  testDevices?: TestDevice[];
  checkIn?: {
    days: (number | string)[];
    fileUploader?: FileUploader[];
  };
  rewards?: {
    showRewardSelectionForm?: number;
    rewardOptions?: {
      team: string[];
      ind: string[];
    };
  };
}

interface Activities {
  participant: Participant;
  coach: Participant;
  leader: Participant;
  order: Order;
  subChallenges?: SubChallenge[];
}

export type CampaignMode = 'SINGLE' | 'MULTI';

export interface CampaignData {
  campaignId: string;
  campaignName: string;
  campaignDescription: string;
  campaignStart: string;
  campaignEnd: string;
  registerEnd?: string;
  loginId: string;
  campaignStatus: string;
  campaignTextLinkDisplay: number;
  campaignTextLink: string;
  // Explicit single-vs-multi switch. Drives admin UI visibility and the BE
  // branch in validateOrderNumber / getWinningCriteriaRules. Legacy campaigns
  // without this flag are treated as SINGLE.
  campaignMode?: CampaignMode;
  activities: Activities;
  [key: string]: DynamicObjectInterface | string | number | Activities;
}

export const campaignStatusList = [
  { value: 'NEW', title: 'NEW' },
  { value: 'ONGOING', title: 'ONGOING' },
  { value: 'CLOSED', title: 'CLOSED' },
];

export const campaignTypeList = [
  { value: 'CHALLENGE', title: 'CHALLENGE' },
  { value: 'PROMOTION', title: 'PROMOTION' },
];

export const campaignModeList = [
  { value: 'SINGLE', title: 'Single Challenge' },
  { value: 'MULTI', title: 'Multiple Sub-Challenges' },
];

export const campaignGameList = [
  { value: 'scratchWin', title: 'SCRATCH AND WIN' },
];

export const pinIdSegmentList = [
  {
    name: 'Class 2 (Platinum and Above)',
    id: 1,
    list: [
      330, 340, 342, 345, 365, 367, 370, 375, 380, 385, 390, 395, 400, 405, 410, 415, 420, 425, 430, 435, 440, 445, 450,
      455, 460, 465, 470,
    ],
  },
  {
    name: 'Class 4 (Gold & Silver Producer)',
    id: 2,
    list: [310, 320],
  },
  {
    name: 'Class 6 (Silver Sponsor)',
    id: 3,
    list: [305],
  },
  {
    name: 'Class 9 (ABO - unqualified or below 21%)',
    id: 4,
    list: [0, 203, 206, 209, 212, 215, 218],
  },
  {
    name: 'APC/Member',
    id: 5,
    list: [-1],
  },
];

type SectionState = {
  // eslint-disable-next-line no-unused-vars
  [key in (typeof Sections)[keyof typeof Sections]]: boolean;
};

const Sections = {
  WinningCriteria: 'winningCriteria',
  FileUploader: 'fileUploader',
  OrderRule: 'orderRule',
  CheckIn: 'checkIn',
} as const;

export const AdminCampaignEditPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { adminToast } = useAdminToast();
  const apiCall = useAPICall();
  const [searchParams] = useSearchParams();
  const recId = searchParams.get('');
  const isNewCampaign = recId === 'new';
  // const [toggleValue, setToggleValue] = useState(false);
  const [campaignIsHealthGoalSectionShown, setCampaignIsHealthGoalSectionShown] = useState(false);
  const [campaignIsProfileImageSectionShown, setCampaignIsProfileImageSectionShown] = useState(false);
  const [certificateRequired, setCertificateRequired] = useState(false);
  const [campaignIsProgressMonitoringSectionShown, setCampaignIsProgressMonitoringSectionShown] = useState(false);
  const [centralizedLocation, setCentralizedLocation] = useState(false);
  const [isPromotion, setIsPromotion] = useState(false);
  const deepCopy = JSON.parse(JSON.stringify(initialCampaignData));
  const [campaignData, setCampaignData] = useState<CampaignData>(deepCopy);
  const [rewardList, setRewardList] = useState<DropDownData[]>([]);
  const [pinList, setPinList] = useState([]);
  const [disabledDates, setDisabledDates] = useState<Date[]>([]);
  const [disabledDataRemovalDates, setDisabledDataRemovalDates] = useState<Date[]>([]);
  const [campaignInvalid, setCampaignInvalid] = useState(true);
  const [campaignClickedFields, setCampaignClickedFields] = useState({
    campaignId: false,
    campaignName: false,
  });
  const [showEdit, setShowEdit] = useState<SectionState>(
    Object.fromEntries(Object.values(Sections).map(section => [section, false])) as SectionState,
  );
  // Index of the sub-challenge whose order/winning-criteria modal is open.
  // null → editing legacy single rule set (activities.order / activities.participant.rules[4]).
  const [editingSubChallengeIndex, setEditingSubChallengeIndex] = useState<number | null>(null);

  const isMy = REACT_APP_COUNTRY === 'MY';
  const subChallenges: SubChallenge[] = campaignData?.activities?.subChallenges ?? [];
  // campaignMode is the single source of truth for which path the admin UI and
  // the BE follow. Legacy data with no mode set falls back to SINGLE.
  const campaignMode: CampaignMode = (campaignData?.campaignMode as CampaignMode) ?? 'SINGLE';
  const usingSubChallenges = isMy && campaignMode === 'MULTI';

  const fetchRewardList = async () => {
    try {
      const result = await apiCall(RewardService.getRewardList);
      // Filter out inactive rewards and store only rewardCode and rewardName
      const activeRewards = result?.data?.data.filter((reward: DynamicObjectInterface) => reward.status === 'ACTIVE');
      const filteredRewards = activeRewards.map((reward: DynamicObjectInterface) => ({
        id: reward.rewardCode,
        name: reward.rewardName,
      }));
      setRewardList(filteredRewards);
      // Handle the filtered reward list data as needed
    } catch (error) {
      LoggerService.log('Failed to retrieve user reward list', error);
      // Handle error appropriately
    }
  };
  const fetchPinList = async () => {
    try {
      const result = await apiCall(PinService.getPinList);
      const pinList = result?.data?.data?.map((pin: Pin) => ({
        id: pin.pinId,
        name: pin.pinName,
      }));
      setPinList(pinList);
      // Handle the reward list data as needed
    } catch (error) {
      LoggerService.log('Failed to retrieve user pin list', error);
      // Handle error appropriately
    }
  };

  const validateCampaign = () => {

    const { campaignId, campaignName, campaignType, registerEnd, dataRemovalDate } = campaignData ?? {};

    if (campaignType === 'PROMOTION'){
        campaignData.dataRemovalDate = '';
        const isAnyFieldEmpty =
        !campaignId ||
        !campaignName
      setCampaignInvalid(isAnyFieldEmpty);
    }else{
      let validTeamSize = true;
      if (campaignData?.activities.participant.participationType?.includes('team') &&
        (!campaignData?.activities.participant.teamSize || campaignData?.activities.participant.teamSize === 0)
      ) {
        validTeamSize = false;
      }

      let validIndReward =
        campaignData?.activities.participant.participationType?.includes('ind') &&
        campaignData?.activities?.participant?.rewards?.showRewardSelectionForm === 1 &&
        (!campaignData?.activities?.participant?.rewards?.rewardOptions?.ind ||
          campaignData?.activities?.participant?.rewards?.rewardOptions?.ind?.length <= 0)
          ? false
          : true;

      let validTeamReward =
        campaignData?.activities.participant.participationType?.includes('team') &&
        campaignData?.activities?.participant?.rewards?.showRewardSelectionForm === 1 &&
        (!campaignData?.activities?.participant?.rewards?.rewardOptions?.team ||
          campaignData?.activities?.participant?.rewards?.rewardOptions?.team?.length <= 0)
          ? false
          : true;

      const isAnyFieldEmpty =
        !campaignId ||
        !campaignName ||
        !registerEnd ||
        !dataRemovalDate ||
        !validTeamSize ||
        !validIndReward ||
        !validTeamReward ||
        !campaignData?.activities.participant.participationType?.length ||
        campaignData?.activities.participant.participationType?.length <= 0;
      setCampaignInvalid(isAnyFieldEmpty);
    }

  };

  const handleInputChangeNormal = (name: string, value: DynamicObjectInterface | DynamicObjectInterface[]) => {
    setCampaignData((prevCampaignData: CampaignData | null) => {
      if (!prevCampaignData) {
        // Initialize the root object if it doesn't exist
        return {
          [name]: value,
        } as CampaignData;
      }

      // Create a copy of the previous campaign data to avoid mutation
      const newData = { ...prevCampaignData };

      // Use a type assertion to handle the dynamic property assignment
      let currentLevel = newData;
      const keys = name.split('.');

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!currentLevel[key]) {
          // Initialize an empty object if the key doesn't exist
          currentLevel[key] = {};
        }
        currentLevel = currentLevel[key];
      }

      // Assign the value to the final key
      currentLevel[keys[keys.length - 1]] = value;

      return { ...newData };
    });

    setCampaignClickedFields(prevClickedFields => ({
      ...prevClickedFields,
      [name]: value === '',
    }));

    if (name === 'campaignType'){
      if (value === 'PROMOTION'){
        setIsPromotion(true);
      }else {
        setIsPromotion(false);
      }
    }

    if (name === 'registerEnd') {
      const campaignStart = new Date(campaignData?.campaignStart);
      const campaignEnd = new Date(campaignData?.campaignEnd);
      if (value < campaignStart || value > campaignEnd) {
        setCampaignInvalid(true);
      }
    } else {
      validateCampaign();
    }
  };

  const handleInputChangeFlatten = (name: string, value: DynamicObjectInterface | DynamicObjectInterface[]) => {
    // Handle comma-separated strings as arrays
    const isArrayValue = Array.isArray(value) || (typeof value === 'string' && value.includes(','));
    if (isArrayValue && typeof value === 'string') {
      value = value.split(',').map(item => item.trim());
    }

    setCampaignData((prevCampaignData: DynamicObjectInterface) => {
      const newData = { ...(prevCampaignData || {}) };
      const keys = name.split('.');
      let currentLevel = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];

        if (typeof currentLevel[key] !== 'object' || currentLevel[key] === null) {
          currentLevel[key] = {};
        }

        currentLevel = currentLevel[key];
      }

      const lastKey = keys[keys.length - 1];
      if (typeof currentLevel === 'object' && currentLevel !== null) {
        currentLevel[lastKey] = value;
      } else {
        LoggerService.log(
          `Cannot assign value to key ${lastKey} because current level is not an object: ${currentLevel}`,
        );
      }
      return newData;
    });

    validateCampaign();
  };

  // Make an empty sub-challenge that mirrors the legacy participant rules[] skeleton
  // so the BE point engine sees the same shape as a single-rule campaign would.
  const makeEmptySubChallenge = (id: string): SubChallenge => ({
    subChallengeId: id,
    name: '',
    description: '',
    winCount: 1,
    order: {
      maxOrderNumber: 1,
      orderPeriod: 30,
      rules: [],
      amount: { value: 0, currency: 'MYR' },
    },
    rules: [
      { actionType: 'WEIGHT_IN', kpiCode: 'WEIGHT_IN_TEAM', description: '', calculationType: 'ONETIME', targetFrom: '3', point: '', targetTo: '0', fullfillmentType: null, reward: '', subKpiCode: [] },
      { actionType: 'WEIGHT_IN', kpiCode: 'NEW_ABO_TEAM', description: '', calculationType: 'ONETIME', targetFrom: '1', point: '', targetTo: '0', fullfillmentType: null, reward: '', subKpiCode: [] },
      { actionType: 'WEIGHT_OUT', kpiCode: 'WEIGHT_OUT_TEAM', description: '', calculationType: 'ONETIME', targetFrom: '3', targetTo: '0', point: '', fullfillmentType: null, reward: '', subKpiCode: [] },
      { actionType: 'WEIGHT_OUT', kpiCode: 'WEIGHT_ACHIEVEMENT_TEAM', description: '', calculationType: 'ONETIME', targetFrom: '0', targetTo: '0', fullfillmentType: 'EITHER', point: '', reward: '', voucher: '', subKpiCode: [
        { subKpiCode: 'WEIGHT_LOST', points: '0', targetFrom: '0', targetTo: '0', indicatorName: '' },
        { subKpiCode: 'BODY_FAT_LOST', points: '0', targetFrom: '0', targetTo: '0', indicatorName: '' },
        { subKpiCode: 'MUSCLE_MASS_GAIN', points: '0', targetFrom: '0', targetTo: '0', indicatorName: '' },
      ]},
      { actionType: 'WEIGHT_OUT', kpiCode: 'WEIGHT_ACHIEVEMENT', description: '', calculationType: 'POINT', targetFrom: '0', targetTo: '0', fullfillmentType: 'EITHER', point: '1', reward: '', voucher: '', subKpiCode: [] },
    ],
    fileUploader: [],
    testDevices: [],
    checkIn: {
      days: [],
      fileUploader: [],
    },
    rewards: {
      showRewardSelectionForm: 0,
      rewardOptions: { team: [], ind: [] },
    },
  });

  const handleAddSubChallenge = () => {
    setCampaignData(prev => {
      const newData = JSON.parse(JSON.stringify(prev)) as CampaignData;
      if (!newData.activities.subChallenges) newData.activities.subChallenges = [];
      const nextIndex = newData.activities.subChallenges.length + 1;
      newData.activities.subChallenges.push(makeEmptySubChallenge(`SC${nextIndex}`));
      return newData;
    });
  };

  const handleDeleteSubChallenge = (index: number) => {
    setCampaignData(prev => {
      const newData = JSON.parse(JSON.stringify(prev)) as CampaignData;
      newData.activities.subChallenges?.splice(index, 1);
      return newData;
    });
  };

  const getPinsFromSelectedSegment = (
    selectedSegments: Pin[],
    pinIdSegmentList: PinIdSegment[],
    fullPinList: { id: number; name: string }[],
  ): Pin[] => {
    const allSegmentPinIds = selectedSegments.flatMap(selected => {
      const matchedSegment = pinIdSegmentList.find(segment => segment.id === selected.pinId);
      return matchedSegment?.list ?? [];
    });

    // Deduplicate pinIds just in case
    const uniquePinIds = Array.from(new Set(allSegmentPinIds));
    return fullPinList
      .filter(pin => uniquePinIds.includes(pin.id))
      .map(pin => ({
        pinId: pin.id,
        pinName: pin.name,
      }));
  };

  const generateDisabledDates = (beforeDate: Date, afterDate: Date | null): Date[] => {
    const disabledDates: Date[] = [];

    // Ensure 'beforeDate' and 'afterDate' are at midnight
    beforeDate.setHours(0, 0, 0, 0);
    afterDate?.setHours(0, 0, 0, 0);

    // Collect all dates before 'beforeDate'
    let inputDate = new Date(beforeDate);
    inputDate.setDate(inputDate.getDate() - 1);

    // Set the earliest date to consider
    const oneYearBefore = new Date(inputDate);
    oneYearBefore.setFullYear(oneYearBefore.getFullYear() - 1);

    while (inputDate >= oneYearBefore) {
      disabledDates.push(new Date(inputDate));
      inputDate.setDate(inputDate.getDate() - 1);
    }

    if (afterDate) {
      // Collect all dates after 'afterDate'
      let futureDate = new Date(afterDate);
      futureDate.setDate(futureDate.getDate() + 1);

      // Set the future limit to one year after 'afterDate'
      const futureLimit = new Date(afterDate);
      futureLimit.setFullYear(afterDate.getFullYear() + 1);

      while (futureDate <= futureLimit) {
        disabledDates.push(new Date(futureDate));
        futureDate.setDate(futureDate.getDate() + 1);
      }
    }

    // Convert to timestamp for comparison and uniqueness
    const uniqueDisabledDates = Array.from(new Set(disabledDates.map(date => date.getTime())));

    return uniqueDisabledDates.map(time => new Date(time));
  };
  useEffect(() => {
    if (campaignData?.campaignEnd) {
      const afterDate = new Date(campaignData?.campaignEnd);
      const disabledDataRemovalDates = generateDisabledDates(
        new Date(new Date(afterDate).setHours(0, 0, 0, 0) + 86400000 + (campaignData.hideSidebarDays ?? 0) * 86400000),
        null,
      );
      setDisabledDataRemovalDates(disabledDataRemovalDates);

      if (campaignData?.campaignStart) {
        const beforeDate = new Date(campaignData?.campaignStart);
        const disabledDates = generateDisabledDates(beforeDate, afterDate);
        setDisabledDates(disabledDates);
      }
    }
  }, [campaignData]);

  // Convert placeholder TEMP-* / empty subKpiCodes into the uppercased indicator
  // name (e.g. "Total Blood Cholesterol" -> "TOTAL_BLOOD_CHOLESTEROL"), mirroring
  // the single-challenge conversion in onSave. Duplicate codes across different
  // sub-challenges are fine — the BE scopes indicators to the matched sub-challenge.
  const normalizeSubChallengeKpiCodes = (subs?: SubChallenge[]) =>
    subs?.map(sc => ({
      ...sc,
      rules: sc.rules?.map((rule, ruleIdx) =>
        ruleIdx === 4
          ? {
              ...rule,
              subKpiCode: rule.subKpiCode?.map(subKpi => ({
                ...subKpi,
                subKpiCode:
                  subKpi.subKpiCode?.includes('TEMP-') || !subKpi.subKpiCode
                    ? subKpi?.indicatorName?.split(' ').join('_').toUpperCase()
                    : subKpi.subKpiCode,
              })),
            }
          : rule,
      ),
      // Resolve the sub-challenge's assessment uploader TEMP- codes to numeric ones,
      // mirroring the base participant.fileUploader handling. Without this, the long
      // TEMP-<timestamp> code overflows the attachment photoType column on upload and
      // the saved proof image can't be matched back to its uploader.
      fileUploader: assignFieldCodes((sc.fileUploader as FileUploader[]) ?? []),
      // Drop empty/invalid check-in days and resolve TEMP- file codes to numeric ones.
      checkIn: sc.checkIn
        ? {
            ...sc.checkIn,
            days: (sc.checkIn.days ?? [])
              .map(d => Number(d))
              .filter(d => !isNaN(d) && d > 0)
              .sort((a, b) => a - b),
            fileUploader: assignFieldCodes((sc.checkIn.fileUploader as FileUploader[]) ?? []),
          }
        : sc.checkIn,
    }));

  function assignFieldCodes(files: FileUploader[]) {
    // Get all numeric fieldCodes as numbers
    const nums = files
      .map(f => {
        const n = Number(f.fieldCode);
        return isNaN(n) ? null : n;
      })
      .filter((n): n is number => n !== null);

    let maxNum = nums.length ? Math.max(...nums) : 0;

    return files.map(file => {
      if (file.fieldCode?.includes('TEMP-')) {
        maxNum++;
        return { ...file, fieldCode: maxNum.toString() };
      }
      return file;
    });
  }

  const onSaveModal = (formData: CampaignData) => {
    const updatedFileUploader = assignFieldCodes(formData.activities.participant.fileUploader ?? []);

    const updatedRules = formData?.activities?.participant?.rules?.map((rule, ruleIdx) => {
      if (ruleIdx === 4) {
        return {
          ...rule,
          subKpiCode: rule.subKpiCode?.map(subKpi => ({
            ...subKpi,
            subKpiCode:
              subKpi.subKpiCode?.includes('TEMP-') || !subKpi.subKpiCode
                ? subKpi?.indicatorName?.split(' ').join('_').toUpperCase()
                : subKpi.subKpiCode,
          })),
        };
      }
      return rule;
    });

    const updatedFormData: CampaignData = {
      ...formData,
      activities: {
        ...formData.activities,
        participant: {
          ...formData.activities.participant,
          fileUploader: updatedFileUploader,
          rules: updatedRules,
        },
      },
    };

    setCampaignData(updatedFormData);

    if (showEdit.winningCriteria) {
      toggleEditModal(Sections.WinningCriteria);
    } else if (showEdit.fileUploader) {
      toggleEditModal(Sections.FileUploader);
    } else if (showEdit.orderRule) {
      toggleEditModal(Sections.OrderRule);
    } else if (showEdit.checkIn) {
      closeModal(Sections.CheckIn);
    }
  };

  const onSave = async (formData?: CampaignData) => {
    const payload: CampaignData = {
      campaignId: campaignData?.campaignId,
      campaignName: campaignData?.campaignName,
      campaignDescription: campaignData?.campaignDescription,
      campaignTextLinkDisplay: campaignData?.campaignTextLinkDisplay,
      campaignIsHealthGoalSectionShown: campaignData?.campaignIsHealthGoalSectionShown ? 1 : 0,
      campaignIsProfileImageSectionShown: campaignData?.campaignIsProfileImageSectionShown ? 1 : 0,
      certificateRequired: campaignData?.certificateRequired ? 1 : 0,
      campaignIsProgressMonitoringSectionShown: campaignData?.campaignIsProgressMonitoringSectionShown ? 1 : 0,
      centralizedLocation: campaignData?.centralizedLocation ? 1 : 0,
      campaignTextLink: campaignData?.campaignTextLink,
      campaignStart: new Date(campaignData?.campaignStart).toISOString().slice(0, -1),
      campaignEnd: new Date(campaignData?.campaignEnd).toISOString().slice(0, -1),
      registerEnd: campaignData?.registerEnd ? convertUTCdate(campaignData.registerEnd) : '',
      loginId: 'admin',
      campaignStatus: campaignData?.campaignStatus,
      campaignType: campaignData?.campaignType,
      // Explicit single/multi switch — drives BE branch in validateOrderNumber
      // and getWinningCriteriaRules. Defaults to SINGLE so legacy/edited rows
      // keep the original behavior even if the admin never opened the dropdown.
      campaignMode: (campaignData?.campaignMode as CampaignMode) ?? 'SINGLE',
      gameType: isPromotion ? campaignData?.gameType : '',
      productSKU: isPromotion ? campaignData?.productSKU : '',
      hideSidebarDays: campaignData?.hideSidebarDays ? Number(campaignData?.hideSidebarDays) : null,
      dataRemovalDate: campaignData?.dataRemovalDate
        ? moment(campaignData?.dataRemovalDate).format('YYYY-MM-DD')
        : null,
      postAssessmentReminderDays: campaignData?.postAssessmentReminderDays
        ? Number(campaignData?.postAssessmentReminderDays)
        : null,
      preAssessmentReminderDays: campaignData?.preAssessmentReminderDays
        ? Number(campaignData?.preAssessmentReminderDays)
        : null,
      activities: {
        participant: {
          name: campaignData?.activities.participant.name,
          description: campaignData?.activities.participant.description,
          status: campaignData?.activities.participant.status,
          participationType: campaignData?.activities.participant.participationType,
          canJoinMultipleTeam: campaignData?.activities?.participant?.canJoinMultipleTeam ?? 0,
          teamSize: campaignData?.activities.participant.teamSize,
          enrollmentType: 'PARTICIPANT',
          newAbo: {
            isRequired: campaignData?.activities?.participant?.newAbo?.isRequired ?? 0,
            numOfDays: campaignData?.activities?.participant?.newAbo?.numOfDays ?? '',
            numOfNewAbo: campaignData?.activities?.participant?.newAbo?.numOfNewAbo ?? '',
          },
          validity: {
            type: 'DAYS',
            value: campaignData?.activities.participant.validity.value,
            bufferDays: 15,
            bufferMonths: 0,
          },
          fileUploader: formData
            ? assignFieldCodes(formData.activities.participant.fileUploader ?? [])
            : campaignData?.activities?.participant?.fileUploader,
          pin: getPinsFromSelectedSegment(campaignData?.activities.participant.pin, pinIdSegmentList, pinList),
          rewards: {
            showRewardSelectionForm: campaignData?.activities?.participant?.rewards?.showRewardSelectionForm ?? 0,
            rewardOptions: campaignData?.activities?.participant?.rewards?.rewardOptions,
          },
          rules: [
            {
              actionType: 'WEIGHT_IN',
              kpiCode: 'WEIGHT_IN_TEAM',
              description: campaignData?.activities.participant.rules[0].description,
              calculationType: 'ONETIME',
              targetFrom: '3',
              point: '',
              targetTo: '0',
              fullfillmentType: null,
              reward: campaignData?.activities.participant.rules[0].reward,
              subKpiCode: [],
            },
            {
              actionType: 'WEIGHT_IN',
              kpiCode: 'NEW_ABO_TEAM',
              description: campaignData?.activities.participant.rules[0].description,
              calculationType: 'ONETIME',
              targetFrom: '1',
              point: '',
              targetTo: '0',
              fullfillmentType: null,
              reward: campaignData?.activities.participant.rules[1].reward,
              subKpiCode: [],
            },
            {
              actionType: 'WEIGHT_OUT',
              kpiCode: 'WEIGHT_OUT_TEAM',
              description: campaignData?.activities.participant.rules[2].description,
              calculationType: 'ONETIME',
              targetFrom: '3',
              targetTo: '0',
              point: '',
              fullfillmentType: null,
              reward: campaignData?.activities.participant.rules[2].reward,
              subKpiCode: [],
            },
            {
              actionType: 'WEIGHT_OUT',
              kpiCode: 'WEIGHT_ACHIEVEMENT_TEAM',
              description: campaignData?.activities.participant.rules[3].description,
              calculationType: 'ONETIME',
              targetFrom: '0',
              targetTo: '0',
              fullfillmentType: 'EITHER',
              point: '',
              reward: campaignData?.activities.participant.rules[3].reward,
              voucher: campaignData?.activities.participant.rules[3].voucher,
              subKpiCode: [
                {
                  subKpiCode: 'WEIGHT_LOST',
                  points: '0',
                  targetFrom: campaignData?.activities.participant.rules[3].subKpiCode[0].targetFrom,
                  targetTo: '0',
                  indicatorName: '',
                },
                {
                  subKpiCode: 'BODY_FAT_LOST',
                  points: '0',
                  targetFrom: campaignData?.activities.participant.rules[3].subKpiCode[1].targetFrom,
                  targetTo: '0',
                  indicatorName: '',
                },
                {
                  subKpiCode: 'MUSCLE_MASS_GAIN',
                  points: '0',
                  targetFrom: campaignData?.activities.participant.rules[3].subKpiCode[2].targetFrom,
                  targetTo: '0',
                  indicatorName: '',
                },
              ],
            },
            {
              actionType: 'WEIGHT_OUT',
              kpiCode: 'WEIGHT_ACHIEVEMENT',
              description: '',
              calculationType: 'POINT',
              targetFrom: '0',
              targetTo: '0',
              fullfillmentType: 'EITHER',
              point: '1',
              reward: '',
              voucher: '',
              subKpiCode: formData
                ? formData?.activities?.participant?.rules[4]?.subKpiCode?.map(subKpi => {
                    return {
                      ...subKpi,
                      subKpiCode:
                        subKpi.subKpiCode?.includes('TEMP-') || !subKpi.subKpiCode
                          ? subKpi?.indicatorName?.split(' ').join('_').toUpperCase()
                          : subKpi.subKpiCode,
                    };
                  })
                : campaignData?.activities?.participant?.rules[4]?.subKpiCode,
            },
          ],
        },
        coach: {
          name: campaignData?.activities.coach.name,
          description: campaignData?.activities.coach.description,
          status: campaignData?.activities.coach.status,
          teamSize: 0,
          enrollmentType: 'COACH',
          validity: {
            type: 'MONTHS',
            value: campaignData?.activities.coach.validity.value,
            bufferDays: 15,
            bufferMonths: 2,
          },
          pin: getPinsFromSelectedSegment(campaignData?.activities.coach.pin, pinIdSegmentList, pinList),
          teamLeaderReminderDays: campaignData?.activities.coach.teamLeaderReminderDays
            ? Number(campaignData?.activities.coach.teamLeaderReminderDays)
            : '',
          rules: [
            {
              actionType: 'COACH_PROCESS',
              kpiCode: 'NEW_ABO_COUNT',
              description: '',
              calculationType: 'COUNT',
              point: '',
              targetFrom: campaignData?.activities.coach.rules[0].targetFrom,
              targetTo: '0',
              fullfillmentType: null,
              reward: null,
              subKpiCode: [],
            },
            {
              actionType: 'COACH_PROCESS',
              kpiCode: 'COMPLETED_COUNT',
              description: '',
              calculationType: 'COUNT',
              point: '',
              targetFrom: campaignData?.activities.coach.rules[1].targetFrom,
              targetTo: '0',
              fullfillmentType: null,
              reward: null,
              subKpiCode: [],
            },
            {
              actionType: 'COACH_PROCESS',
              kpiCode: 'ACHIEVED_COUNT',
              calculationType: 'COUNT',
              description: '',
              point: '',
              targetFrom: campaignData?.activities.coach.rules[2].targetFrom,
              targetTo: '0',
              fullfillmentType: null,
              reward: null,
              subKpiCode: [],
            },
            {
              actionType: 'COACH_PROCESS',
              kpiCode: 'ALP_COUNT',
              calculationType: 'COUNT',
              description: '',
              point: '',
              targetFrom: campaignData?.activities.coach.rules[3].targetFrom,
              targetTo: '0',
              fullfillmentType: null,
              reward: null,
              subKpiCode: [],
            },
          ],
        },
        leader: {
          name: campaignData?.activities.leader.name,
          description: campaignData?.activities.leader.description,
          status: campaignData?.activities.leader.status,
          teamSize: 0,
          enrollmentType: 'LEADER',
          validity: {
            type: 'MONTHS',
            value: campaignData?.activities.leader.validity.value,
            bufferDays: 15,
            bufferMonths: 0,
          },
          pin: campaignData?.activities.leader.pin,
          rules: [
            {
              actionType: 'LEADER_PROCESS',
              kpiCode: 'QUALIFIED_COACH',
              calculationType: 'COUNT',
              description: '',
              targetFrom: campaignData?.activities.leader.rules[0].targetFrom,
              targetTo: '0',
              point: '',
              fullfillmentType: null,
              reward: 'TRIP2',
              subKpiCode: [],
            },
            {
              actionType: 'LEADER_PROCESS',
              kpiCode: 'NEW_ABO_COUNT_LEADER',
              calculationType: 'COUNT',
              description: '',
              targetFrom: '0',
              targetTo: '0',
              point: '',
              fullfillmentType: null,
              reward: '',
              subKpiCode: [],
            },
            {
              actionType: 'LEADER_PROCESS',
              kpiCode: 'NEW_GROUP_ABO_COUNT_LEADER',
              calculationType: 'COUNT',
              description: '',
              targetFrom: '0',
              targetTo: '0',
              point: '',
              fullfillmentType: null,
              reward: '',
              subKpiCode: [],
            },
            {
              actionType: 'LEADER_PROCESS',
              kpiCode: 'CUMULATIVE_POINTS_LEADER',
              calculationType: 'POINT',
              description: '',
              targetFrom: '0',
              targetTo: '0',
              point: '',
              fullfillmentType: null,
              reward: '',
              subKpiCode: [],
            },
          ],
        },
        order: {
          maxOrderNumber: Number(campaignData?.activities.order.maxOrderNumber),
          orderPeriod: campaignData?.activities.order.orderPeriod,
          rules: formData ? formData?.activities.order.rules : campaignData?.activities.order.rules,
          amount: campaignData?.activities.order.amount,
        },
        // Sub challenges (MY only). When present, the BE evaluates each entry's
        // order rules at registration and persists the matching subChallengeId
        // onto the enrollment row. Win threshold from subChallenges[i].winCount.
        ...(formData?.activities?.subChallenges
          ? { subChallenges: normalizeSubChallengeKpiCodes(formData.activities.subChallenges) }
          : campaignData?.activities?.subChallenges && campaignData.activities.subChallenges.length > 0
            ? { subChallenges: normalizeSubChallengeKpiCodes(campaignData.activities.subChallenges) }
            : {}),
      },
    };

    try {
      let result;

      if (isNewCampaign) {
        result = await apiCall(CampaignService.addCampaign, payload);
      } else {
        result = await apiCall(CampaignService.updateCampaign, `${recId}`, payload);
      }

      if (!result?.data?.result) {
        // Handle duplicate campaign ID error
        adminToast({
          message: result?.data?.message ?? t('admin.campaignConfig.duplicateCampaignId'),
          theme: Theme.ERROR,
        });
      } else {
        // Success case
        if (showEdit.winningCriteria) {
          fetchData();
          toggleEditModal(Sections.WinningCriteria);
        } else if (showEdit.fileUploader) {
          fetchData();
          toggleEditModal(Sections.FileUploader);
        } else if (showEdit.orderRule) {
          fetchData();
          toggleEditModal(Sections.OrderRule);
        } else if (showEdit.checkIn) {
          fetchData();
          toggleEditModal(Sections.CheckIn);
        } else {
          navigate(`/admin/master/campaign-configuration/list`);
        }

        adminToast({
          message: isNewCampaign
            ? t('admin.campaignConfig.createCampaignSuccess')
            : t('admin.campaignConfig.updateCampaignSuccess'),
          theme: Theme.SUCCESS,
        });
      }
    } catch (error) {
      LoggerService.log('Error saving campaign', error);
      adminToast({
        message: t('admin.campaignConfig.errorSaveCampaign'),
        theme: Theme.ERROR,
      });
    }
  };

  const buttonConfig = [
    {
      title: t('admin.userEdit.cancel'),
      buttonStyle: ButtonStyles.SOLID,
      onClick: () => {
        navigate(`/admin/master/campaign-configuration/list`);
      },
    },
    {
      title: t('admin.userEdit.save'),
      buttonStyle: ButtonStyles.SOLID,
      disabled: campaignInvalid,
      onClick: () => {
        if (!formatCampaignId(campaignData?.campaignId)) {
          adminToast({
            message: t('Only letters (A-Z), numbers, and underscores (_) are allowed in Campaign ID.'),
            theme: Theme.ERROR,
          });
          return;
        }

        if (!isPromotion && (
          !campaignData?.registerEnd ||
          new Date(campaignData?.registerEnd) < new Date(campaignData?.campaignStart) ||
          new Date(campaignData?.registerEnd) > new Date(campaignData?.campaignEnd))
        ) {
          adminToast({
            message: t(
              'Unable to save changes. The Registration End Date should be between the Campaign Start Date and Campaign End Date',
            ),
            theme: Theme.ERROR,
          });
          return;
        }

        const campaignEndDatePlusExtra = new Date(
          new Date(campaignData?.campaignEnd).setHours(0, 0, 0, 0) +
            86400000 +
            (campaignData.hideSidebarDays ?? 0) * 86400000,
        );
        if (!isPromotion && (
          !campaignData?.dataRemovalDate ||
          new Date(campaignData?.dataRemovalDate) < new Date(campaignEndDatePlusExtra))
        ) {
          adminToast({
            message: t(
              'Unable to save changes. The Data Removal Date should be after Campaign End Date and Challenge hidden from sidebar',
            ),
            theme: Theme.ERROR,
          });
          return;
        }

        if (
          campaignData?.activities.participant.participationType?.includes('team') &&
          (!campaignData?.activities.participant.teamSize || campaignData?.activities.participant.teamSize === 0)
        ) {
          return;
        }

        onSave();
      },
    },
  ];
  const fetchData = async () => {
    try {
      const result = await apiCall(CampaignService.getCampaignDetail, `${recId}`);
      const campaignData = result?.data?.data;
      // Check if registerEnd exists and convert it
      if (campaignData?.registerEnd === 'Invalid date') {
        campaignData.registerEnd = '';
      }

      if (campaignData?.registerEnd) {
        campaignData.registerEnd = convertLocalDate(campaignData.registerEnd, 'YYYY-MM-DDTHH:mm:ss.SSS');
      }

      if (campaignData?.campaignType === 'PROMOTION'){
        setIsPromotion(true);
      }

      if (campaignData?.activities?.participant?.pin) {
        const selectedPinIds = campaignData.activities.participant.pin.map((pin: Pin) => pin.pinId);
        const pinSegments: Pin[] = [];

        pinIdSegmentList.forEach(segment => {
          const hasAny = segment.list.some(pinId => selectedPinIds.includes(pinId));
          if (hasAny) {
            pinSegments.push({
              pinId: segment.id,
              pinName: segment.name,
            });
          }
        });

        campaignData.activities.participant.pin = pinSegments;
      }

      if (campaignData?.activities?.coach?.pin) {
        const selectedPinIds = campaignData.activities.coach.pin.map((pin: Pin) => pin.pinId);
        const pinSegments: Pin[] = [];

        pinIdSegmentList.forEach(segment => {
          const hasAny = segment.list.some(pinId => selectedPinIds.includes(pinId));
          if (hasAny) {
            pinSegments.push({
              pinId: segment.id,
              pinName: segment.name,
            });
          }
        });

        campaignData.activities.coach.pin = pinSegments;
      }

      setCampaignData(campaignData);
    } catch (error) {
      LoggerService.log('Failed to retrieve campaign details', error);
      // Handle error appropriately
    }
  };

  useEffect(() => {
    validateCampaign();
  }, [campaignData]);

  useEffect(() => {
    if(!isPromotion){
      fetchRewardList();
      fetchPinList();
    }

    if (!isNewCampaign) {
      fetchData();
    }
  }, [recId, isNewCampaign]);

  const toggleEditModal = (section: (typeof Sections)[keyof typeof Sections]) => {
    setShowEdit(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const closeModal = (section: (typeof Sections)[keyof typeof Sections]) => {
    toggleEditModal(section);
    setEditingSubChallengeIndex(null);
  };

  const showModal = (
    <>
      {showEdit.winningCriteria && (
        <WinningCriteriaModal
          campaignData={campaignData}
          onCancel={() => closeModal(Sections.WinningCriteria)}
          onSave={isNewCampaign ? onSaveModal : onSave}
          subChallengeIndex={editingSubChallengeIndex ?? undefined}
        />
      )}
      {showEdit.fileUploader && (
        <FileUploaderModal
          campaignData={campaignData}
          onCancel={() => closeModal(Sections.FileUploader)}
          onSave={isNewCampaign ? onSaveModal : onSave}
          subChallengeIndex={editingSubChallengeIndex ?? undefined}
        />
      )}
      {showEdit.orderRule && (
        <OrderRuleModal
          campaignData={campaignData}
          onCancel={() => closeModal(Sections.OrderRule)}
          onSave={isNewCampaign ? onSaveModal : onSave}
          subChallengeIndex={editingSubChallengeIndex ?? undefined}
        />
      )}
      {showEdit.checkIn && (
        <CheckInModal
          campaignData={campaignData}
          onCancel={() => closeModal(Sections.CheckIn)}
          onSave={isNewCampaign ? onSaveModal : onSave}
          subChallengeIndex={editingSubChallengeIndex ?? undefined}
        />
      )}
    </>
  );

  return (
    <div className={styles['campaign-container']}>
      <AdminPageHeader
        title={isNewCampaign ? t('admin.campaignConfig.newTitle') : t('admin.campaignConfig.editTitle')}
        button={buttonConfig}
      />
      {/* campaign section  */}
      {/* 1st row info  */}

      <h2 className={`${styles['accordion-header']}`} id={`campaign-details-accordion-header`}>
        <div className={`${styles['accordion-button']}`}>
          <span className={styles['accordion-label-left']}>
            <label className={`${styles['accordion-label']} ${styles[FontSize.LARGE]}`}>{'Campaign Details'}</label>
          </span>
        </div>
      </h2>

      <div className={styles['edit-user-form-container']}>
        <div className={styles['input-container']}>
          <Input
            type={InputTypes.TEXT}
            name={'campaignId'}
            placeholder={t('admin.campaignConfig.campaignId')}
            label={`${t('admin.campaignConfig.campaignId')}*`}
            disabled={!isNewCampaign}
            value={campaignData?.campaignId}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              const trimmedValue = e.target.value.trim();
              handleInputChangeNormal('campaignId', trimmedValue);
            }}
            onClick={() =>
              setCampaignClickedFields(prevClickedFields => ({
                ...prevClickedFields,
                campaignId: campaignData?.campaignId === '',
              }))
            }
            labelInfo="Do not include any spacing in the campaign ID"
            status={campaignClickedFields.campaignId ? InputValidations.INVALID : InputValidations.DEFAULT}
            message={campaignClickedFields.campaignId ? t('admin.campaignConfig.campaignIdReq') : ''}
          />
        </div>
        <div className={styles['input-container']}>
          <DropDown
            name={'campaignType'}
            dropdownLabel={`${t('admin.campaignConfig.campaignType')}`}
            mlpVersion={MLPVersion.ONE}
            data={campaignTypeList}
            value={campaignData?.campaignType}
            configData={{
              label: 'title',
              onSelect: (selectedOptions: DropDownDataInterface) => {
                handleInputChangeNormal('campaignType', selectedOptions.title);
              },
            }}
          />
        </div>
        {isMy && !isPromotion && (
          <div className={styles['input-container']}>
            <DropDown
              name={'campaignMode'}
              dropdownLabel={'Challenge Mode'}
              mlpVersion={MLPVersion.ONE}
              data={campaignModeList}
              value={campaignModeList.find(m => m.value === campaignMode)?.title}
              configData={{
                label: 'title',
                onSelect: (selectedOptions: DropDownDataInterface) => {
                  handleInputChangeNormal('campaignMode', selectedOptions.value);
                },
              }}
            />
          </div>
        )}
        <div className={styles['input-container']}>
          <Input
            type={InputTypes.TEXT}
            name={'campaignName'}
            placeholder={t('admin.campaignConfig.campaignName')}
            label={`${t('admin.campaignConfig.campaignName')}*`}
            value={campaignData?.campaignName}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChangeNormal('campaignName', e.target.value);
            }}
            onClick={() =>
              setCampaignClickedFields(prevClickedFields => ({
                ...prevClickedFields,
                campaignName: campaignData?.campaignName === '',
              }))
            }
            status={campaignClickedFields.campaignName ? InputValidations.INVALID : InputValidations.DEFAULT}
            message={campaignClickedFields.campaignName ? t('admin.campaignConfig.campaignNameReq') : ''}
          />
        </div>
        <div className={styles['input-container']}>
          <DropDown
            name={'campaignStatus'}
            dropdownLabel={`${t('admin.campaignConfig.campaignStatus')}`}
            mlpVersion={MLPVersion.ONE}
            data={campaignStatusList}
            value={campaignData?.campaignStatus}
            configData={{
              label: 'title',
              onSelect: (selectedOptions: DropDownDataInterface) => {
                handleInputChangeNormal('campaignStatus', selectedOptions.title);
              },
            }}
          />
        </div>

        {/* 2nd row description only  */}
        {/* <div className={styles['edit-user-form-container']}> */}
        {/* </div> */}
        <div className={styles['input-container']}>
          <DatePicker
            inputContainerHeight="36px"
            showDateContainer
            calendarHeading={t('admin.campaignConfig.startDate')}
            value={new Date(campaignData?.campaignStart)}
            onChange={date => {
              handleInputChangeNormal('campaignStart', date);
            }}
            actionButtonStyle={{ padding: '5px 10px 0' }}
            menuContainerStyle={{ display: 'flex', justifyContent: 'flex-end' }}
            calendarContainerStyling={{ padding: '10px', top: '-150px' }}
            containerWidth="450px"
            timeLabel={t('admin.campaignConfig.selectTime')}
            timeLabelEnd={t('admin.campaignConfig.selectTimeEnd')}
            timeLabelStart={t('admin.campaignConfig.selectTimeStart')}
            inputLabel={`${t('admin.campaignConfig.startDate')}*`}
          />
        </div>

        <div className={styles['input-container']}>
          <DatePicker
            showDateContainer
            inputContainerHeight="36px"
            calendarHeading={t('admin.campaignConfig.endDate')}
            value={new Date(campaignData?.campaignEnd)}
            onChange={date => {
              if (date) {
                handleInputChangeNormal('campaignEnd', date);
                const dataRemovalDate = new Date(date.getTime());
                dataRemovalDate.setDate(
                  dataRemovalDate.getDate() +
                    180 +
                    (campaignData?.hideSidebarDays ? parseInt(campaignData?.hideSidebarDays) : 0),
                );
                handleInputChangeNormal('dataRemovalDate', dataRemovalDate);
              }
            }}
            containerWidth="450px"
            menuContainerStyle={{ display: 'flex', justifyContent: 'flex-end' }}
            actionButtonStyle={{ padding: '5px 10px 0' }}
            calendarContainerStyling={{ padding: '10px', top: '-150px' }}
            timeLabel={t('admin.campaignConfig.selectTime')}
            timeLabelEnd={t('admin.campaignConfig.selectTimeEnd')}
            timeLabelStart={t('admin.campaignConfig.selectTimeStart')}
            inputLabel={`${t('admin.campaignConfig.endDate')}*`}
          />
        </div>
        <div className={styles['input-container']}>
          <TextArea
            mlpVersion={MLPVersion.ONE}
            placeholder={t('admin.campaignConfig.description')}
            label={t('admin.campaignConfig.description')}
            resize={false}
            value={campaignData?.campaignDescription}
            onChange={e => {
              handleInputChangeNormal('campaignDescription', e.target.value);
            }}
          />
        </div>
        {isPromotion ? (
          <div className={styles['input-container']}>
          <DropDown
            name={'gameType'}
            dropdownLabel={`${t('admin.campaignConfig.gameType')}`}
            mlpVersion={MLPVersion.ONE}
            data={campaignGameList}
            value={campaignData?.gameType}
            configData={{
              label: 'title',
              onSelect: (selectedOptions: DropDownDataInterface) => {
                handleInputChangeNormal('gameType', selectedOptions.title);
              },
            }}
          />
        </div> 
        ): (
          <div>
            <div className={styles['input-container']}>
            <DatePicker
              inputContainerHeight="36px"
              showDateContainer
              showTime
              calendarHeading={t('admin.campaignConfig.registrationEnd')}
              value={campaignData?.registerEnd ? new Date(campaignData.registerEnd) : undefined}
              onChange={datetime => {
                handleInputChangeNormal('registerEnd', datetime);
              }}
              actionButtonStyle={{ padding: '5px 10px 0' }}
              menuContainerStyle={{ display: 'flex', justifyContent: 'flex-end' }}
              calendarContainerStyling={{ padding: '10px', top: '-120px' }}
              containerWidth="450px"
              timeLabel={t('admin.campaignConfig.selectTime')}
              timeLabelEnd={t('admin.campaignConfig.selectTimeEnd')}
              timeLabelStart={t('admin.campaignConfig.selectTimeStart')}
              disabledDates={disabledDates}
              inputLabel={`${t('admin.campaignConfig.registrationEnd')}*`}
            />
          </div>
          <br/>
          <div></div>
          <div className={styles['input-container']}>
            <DatePicker
              showDateContainer
              inputContainerHeight="36px"
              calendarHeading={`Data Removal Date`}
              value={campaignData?.dataRemovalDate ? new Date(campaignData?.dataRemovalDate) : undefined}
              onChange={date => {
                handleInputChangeNormal('dataRemovalDate', date);
              }}
              containerWidth="450px"
              menuContainerStyle={{ display: 'flex', justifyContent: 'flex-end' }}
              actionButtonStyle={{ padding: '5px 10px 0' }}
              calendarContainerStyling={{ padding: '10px', top: '5px' }}
              inputLabel={`Data Removal Date*`}
              disabledDates={disabledDataRemovalDates}
              labelInfo="Note: By default, all health metrics data (eg front/side view photos, pre/post-assessment values and file uploads, progress monitoring values) will be removed on this date, unless otherwise specified according to market legal requirements."
            />
          </div>
        </div>
        )}
      </div>
      <br />

      {isPromotion ? (
        <div>
          <Accordion
              key={'campaignMechanics'}
              id={'campaignMechanics'}
              iconType={ControlIconType.CHEVRON}
              title={`Campaign Mechanics`}
            >
              <>
                <div className={styles['edit-user-form-container']}>
                  <div className={styles['input-container']}>
                    <div className={styles['label-text']}>Centralised Distribution Location</div>
                    <div className={styles['text-link-container']}>
                      <div className={styles['switch-container']}>
                        <Toggle
                          toggleStyle={ToggleStyle.SWITCH}
                          disabled={false}
                          isActive={campaignData?.centralizedLocation === 1}
                          onClick={() => {
                            setCentralizedLocation(!centralizedLocation);
                            handleInputChangeNormal(
                              'centralizedLocation',
                              centralizedLocation ? 0 : 1,
                            );
                          }}
                          theme={Theme.PURPLE}
                        />
                      </div>
                    </div>
                  </div>
                </div>
               <div className={styles['reward-header-label']}>{t('admin.campaignConfig.orderValidation')}</div>
                  <div className={styles['input-container']}>
                    <Input
                      type={InputTypes.TEXT}
                      name={'productSKU'}
                      label={`${t('admin.campaignConfig.singleProductSKU')}`}
                      value={campaignData?.productSKU}
                      mlpVersion={MLPVersion.ONE}
                      onChange={e => {
                        handleInputChangeFlatten('productSKU', e.target.value.toString());
                      }}
                    />
                </div>
              </>
            </Accordion>
        </div>
      ): 
      (
        <div>
            <Accordion
              key={'campaignMechanics'}
              id={'campaignMechanics'}
              iconType={ControlIconType.CHEVRON}
              title={`Campaign Mechanics`}
            >
              <>
                <div className={styles['reward-header-label']}>
                  <div>Participation Type</div>
                </div>
                <div className={styles['edit-user-form-container']}>
                  <div className={styles['input-container']}>
                    {/* <div className={styles['input-label']}>Participation Type*</div> */}
                    <div className={styles['checkbox-container']}>
                      {['team', 'ind'].map(type => (
                        <div className={styles['checkbox-container-box']} key={type}>
                          <div>
                            <input
                              id={type}
                              className={styles['checkbox-container-checkbox']}
                              type="checkbox"
                              checked={campaignData?.activities.participant.participationType?.includes(type)}
                              onChange={e => {
                                const currentTypes = campaignData?.activities.participant.participationType || [];
                                const newTypes = e.target.checked
                                  ? [...currentTypes, type]
                                  : currentTypes.filter(t => t !== type);

                                handleInputChangeNormal('activities.participant.participationType', newTypes);
                              }}
                            />
                            <label className={styles['checkbox-container-label']} htmlFor={type}>
                              {type === 'ind' ? 'Individual' : 'Team'}
                            </label>
                          </div>

                          {type === 'team' && campaignData?.activities.participant.participationType?.includes('team') && (
                            <div className={styles['input-container']}>
                              <Input
                                type={InputTypes.NUMBER}
                                name="teamMember"
                                label="Number of Team Members Required*"
                                value={campaignData?.activities.participant.teamSize}
                                mlpVersion={MLPVersion.ONE}
                                onChange={e => {
                                  handleInputChangeNormal('activities.participant.teamSize', e.target.value);
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={styles['input-container']}>
                    <Input
                      type={InputTypes.NUMBER}
                      name={'participant days'}
                      label={`${t('admin.campaignConfig.participantDays')}`}
                      value={campaignData?.activities.participant.validity.value}
                      mlpVersion={MLPVersion.ONE}
                      onChange={e => {
                        handleInputChangeNormal('activities.participant.validity.value', e.target.value);
                      }}
                    />
                  </div>
                  <div className={styles['input-container']}>
                    <div className={styles['label-text']}>Can Join Multiple Team</div>
                    <div className={styles['text-link-container']}>
                      <div className={styles['switch-container']}>
                        <Toggle
                          toggleStyle={ToggleStyle.SWITCH}
                          isActive={campaignData?.activities?.participant?.canJoinMultipleTeam === 1}
                          onClick={() => {
                            handleInputChangeFlatten(
                              'activities.participant.canJoinMultipleTeam',
                              campaignData?.activities?.participant?.canJoinMultipleTeam ? 0 : 1,
                            );
                          }}
                          disabled={
                            !campaignData?.activities.participant.participationType ||
                            campaignData?.activities.participant.participationType?.length === 0
                          }
                          theme={Theme.PURPLE}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <br />

                {!usingSubChallenges && (
                  <>
                <div className={styles['reward-header-label']}>{'Reward Configuration'}</div>
                <div className={styles['edit-user-form-container']}>
                  <div className={styles['input-container']}>
                    <div className={styles['label-text']}>Show Reward Selection Form</div>
                    <div className={styles['text-link-container']}>
                      <div className={styles['switch-container']}>
                        <Toggle
                          toggleStyle={ToggleStyle.SWITCH}
                          isActive={campaignData?.activities?.participant?.rewards?.showRewardSelectionForm === 1}
                          onClick={() => {
                            handleInputChangeFlatten(
                              'activities.participant.rewards.showRewardSelectionForm',
                              campaignData?.activities?.participant?.rewards?.showRewardSelectionForm ? 0 : 1,
                            );
                          }}
                          disabled={
                            !campaignData?.activities.participant.participationType ||
                            campaignData?.activities.participant.participationType?.length === 0
                          }
                          theme={Theme.PURPLE}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles['edit-user-form-container']}>
                  <div className={styles['input-container']}>
                    <DropDown
                      id={'rewardIndividual'}
                      key={'rewardIndividual'}
                      name={'rewardIndividual'}
                      dropdownLabel={'Reward Options - Individual Participants*'}
                      placeholder={'Reward Options - Individual Participants'}
                      mlpVersion={MLPVersion.ONE}
                      value={campaignData?.activities?.participant?.rewards?.rewardOptions?.ind
                        ?.map(code => {
                          const match = rewardList.find(r => r.id === code);
                          return match?.name ?? null;
                        })
                        .filter((name): name is string => typeof name === 'string')}
                      data={rewardList}
                      multiSelection
                      allSelectionOption
                      configData={{
                        label: 'name',
                        onSelect: (selectedOptions: DropDownDataInterface[]) => {
                          const transformedOptions = selectedOptions.map(option => option.id);
                          handleInputChangeFlatten('activities.participant.rewards.rewardOptions.ind', [
                            ...transformedOptions,
                          ]);
                        },
                      }}
                      disabled={!campaignData?.activities.participant.participationType?.includes('ind')}
                      status={
                        campaignData?.activities.participant.participationType?.includes('ind') &&
                        campaignData?.activities?.participant?.rewards?.showRewardSelectionForm === 1 &&
                        (!campaignData?.activities?.participant?.rewards?.rewardOptions?.ind ||
                          campaignData?.activities?.participant?.rewards?.rewardOptions?.ind?.length <= 0)
                          ? DropDownValidations.INVALID
                          : DropDownValidations.DEFAULT
                      }
                      message={
                        campaignData?.activities.participant.participationType?.includes('ind') &&
                        campaignData?.activities?.participant?.rewards?.showRewardSelectionForm === 1 &&
                        (!campaignData?.activities?.participant?.rewards?.rewardOptions?.ind ||
                          campaignData?.activities?.participant?.rewards?.rewardOptions?.ind?.length <= 0)
                          ? 'Please configure the reward options.'
                          : ''
                      }
                    />
                  </div>
                  <div className={styles['input-container']}>
                    <DropDown
                      id={'rewardTeam'}
                      key={'rewardTeam'}
                      name={'rewardTeam'}
                      dropdownLabel={'Reward Options - Team Participants*'}
                      placeholder={'Reward Options - Team Participants'}
                      mlpVersion={MLPVersion.ONE}
                      value={campaignData?.activities?.participant?.rewards?.rewardOptions?.team
                        ?.map(code => {
                          const match = rewardList.find(r => r.id === code);
                          return match?.name ?? null;
                        })
                        .filter((name): name is string => typeof name === 'string')}
                      data={rewardList}
                      multiSelection
                      allSelectionOption
                      configData={{
                        label: 'name',
                        onSelect: (selectedOptions: DropDownDataInterface[]) => {
                          const transformedOptions = selectedOptions.map(option => option.id);
                          handleInputChangeFlatten('activities.participant.rewards.rewardOptions.team', [
                            ...transformedOptions,
                          ]);
                        },
                      }}
                      disabled={!campaignData?.activities.participant.participationType?.includes('team')}
                      status={
                        campaignData?.activities.participant.participationType?.includes('team') &&
                        campaignData?.activities?.participant?.rewards?.showRewardSelectionForm === 1 &&
                        (!campaignData?.activities?.participant?.rewards?.rewardOptions?.team ||
                          campaignData?.activities?.participant?.rewards?.rewardOptions?.team?.length <= 0)
                          ? DropDownValidations.INVALID
                          : DropDownValidations.DEFAULT
                      }
                      message={
                        campaignData?.activities.participant.participationType?.includes('team') &&
                        campaignData?.activities?.participant?.rewards?.showRewardSelectionForm === 1 &&
                        (!campaignData?.activities?.participant?.rewards?.rewardOptions?.team ||
                          campaignData?.activities?.participant?.rewards?.rewardOptions?.team?.length <= 0)
                          ? 'Please configure the reward options.'
                          : ''
                      }
                    />
                  </div>
                </div>
                  </>
                )}

                <br />

                <div className={styles['reward-header-label']}>{t('admin.campaignConfig.orderValidation')}</div>

                <div className={styles['edit-user-form-container-three-columns']}>
                  {/* points   */}
                  <div className={styles['input-container']}>
                    <Input
                      type={InputTypes.NUMBER}
                      name={'maxOrder'}
                      label={`${t('admin.campaignConfig.maxOrder')}`}
                      value={campaignData?.activities?.order?.maxOrderNumber}
                      mlpVersion={MLPVersion.ONE}
                      onChange={e => {
                        handleInputChangeFlatten('activities.order.maxOrderNumber', e.target.value.toString());
                      }}
                    />
                  </div>
                  <div className={styles['input-container']}>
                    <Input
                      type={InputTypes.NUMBER}
                      name={'orderPeriod'}
                      label={`${t('admin.campaignConfig.orderPeriod')}`}
                      value={campaignData?.activities?.order?.orderPeriod}
                      mlpVersion={MLPVersion.ONE}
                      onChange={e => {
                        handleInputChangeFlatten('activities.order.orderPeriod', e.target.value.toString());
                      }}
                    />
                  </div>
                  <div className={styles['input-container']}>
                    <Input
                      type={InputTypes.NUMBER}
                      name={'orderAmount'}
                      label={`${t('admin.campaignConfig.orderAmount')}`}
                      value={campaignData?.activities.order?.amount?.value}
                      mlpVersion={MLPVersion.ONE}
                      onChange={e => {
                        handleInputChangeFlatten('activities.order.amount.value', e.target.value.toString());
                      }}
                    />
                  </div>
                </div>

                <br />

                {isMy && campaignMode === 'MULTI' && (
                  <>
                    <div className={styles['reward-header-label']}>
                      <div>{'Sub Challenges'}</div>
                      <IconButton
                        iconName="plus"
                        iconStyle={IconStyle.SOLID}
                        theme={Theme.WHITE}
                        onClick={handleAddSubChallenge}
                      />
                    </div>
                    {subChallenges.map((sc, scIndex) => (
                      <Card key={sc.subChallengeId ?? `sc-${scIndex}`}>
                        <div className={styles['order-rules-container-headline']}>
                          <div>{sc.subChallengeId || `Sub Challenge ${scIndex + 1}`}</div>
                          <IconButton
                            iconName="trash-can"
                            iconStyle={IconStyle.SOLID}
                            theme={Theme.WHITE}
                            onClick={() => handleDeleteSubChallenge(scIndex)}
                          />
                        </div>
                        <div className={styles['edit-user-form-container']}>
                          <div className={styles['input-container']}>
                            <Input
                              type={InputTypes.TEXT}
                              name={`sc-${scIndex}-name`}
                              label={'Sub Challenge Name*'}
                              value={sc.name}
                              mlpVersion={MLPVersion.ONE}
                              onChange={e =>
                                handleInputChangeNormal(
                                  `activities.subChallenges.${scIndex}.name`,
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                          <div className={styles['input-container']}>
                            <Input
                              type={InputTypes.TEXT}
                              name={`sc-${scIndex}-description`}
                              label={'Description'}
                              value={sc.description ?? ''}
                              mlpVersion={MLPVersion.ONE}
                              onChange={e =>
                                handleInputChangeNormal(
                                  `activities.subChallenges.${scIndex}.description`,
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                          <div className={styles['input-container']}>
                            <Input
                              type={InputTypes.TEXT}
                              name={`sc-${scIndex}-assessmentTitle`}
                              label={'Assessment Title'}
                              labelInfo="Heading shown above the assessment table on the participant's My Progress page. Leave blank to use the default."
                              value={sc.assessmentTitle ?? ''}
                              mlpVersion={MLPVersion.ONE}
                              onChange={e =>
                                handleInputChangeNormal(
                                  `activities.subChallenges.${scIndex}.assessmentTitle`,
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                          <div className={styles['input-container']}>
                            <Input
                              type={InputTypes.NUMBER}
                              name={`sc-${scIndex}-winCount`}
                              label={'Indicators to Win*'}
                              labelInfo="Number of indicators a participant must achieve to be considered a winner"
                              value={sc.winCount ?? ''}
                              mlpVersion={MLPVersion.ONE}
                              onChange={e =>
                                handleInputChangeNormal(
                                  `activities.subChallenges.${scIndex}.winCount`,
                                  e.target.value === '' ? '' : Number(e.target.value),
                                )
                              }
                            />
                          </div>
                        </div>
                        <div className={styles['sub-challenge-buy-bundle']}>
                          <Input
                            type={InputTypes.TEXT}
                            name={`sc-${scIndex}-buyBundleLink`}
                            label={'Buy Bundle Link'}
                            labelInfo="URL opened by the 'Buy Bundles' button on the check-eligibility tiles. Leave blank to use the default link (https://amway.my)"
                            value={sc.buyBundleLink ?? ''}
                            mlpVersion={MLPVersion.ONE}
                            onChange={e =>
                              handleInputChangeNormal(
                                `activities.subChallenges.${scIndex}.buyBundleLink`,
                                e.target.value,
                              )
                            }
                          />
                          <div className={styles['sub-challenge-buy-bundle-hint']}>
                            example: https://amway.my
                          </div>
                        </div>
                        <div className={styles['order-rules-container-headline']}>
                          <div>{`Order Rules (${sc.order?.rules?.length ?? 0} group(s))`}</div>
                          <IconButton
                            iconName="pencil"
                            iconStyle={IconStyle.SOLID}
                            theme={Theme.WHITE}
                            onClick={() => {
                              setEditingSubChallengeIndex(scIndex);
                              toggleEditModal(Sections.OrderRule);
                            }}
                          />
                        </div>
                        <div className={styles['order-rules-container-headline']}>
                          <div>{`Assessment Indicators (${sc.rules?.[4]?.subKpiCode?.length ?? 0} configured)`}</div>
                          <IconButton
                            iconName="pencil"
                            iconStyle={IconStyle.SOLID}
                            theme={Theme.WHITE}
                            onClick={() => {
                              setEditingSubChallengeIndex(scIndex);
                              toggleEditModal(Sections.WinningCriteria);
                            }}
                          />
                        </div>
                        <div className={styles['order-rules-container-headline']}>
                          <div>{`Assessment File Upload (${sc.fileUploader?.length ?? 0} configured)`}</div>
                          <IconButton
                            iconName="pencil"
                            iconStyle={IconStyle.SOLID}
                            theme={Theme.WHITE}
                            onClick={() => {
                              setEditingSubChallengeIndex(scIndex);
                              toggleEditModal(Sections.FileUploader);
                            }}
                          />
                        </div>
                        <div className={styles['order-rules-container-headline']}>
                          <div>{`Check-In (${sc.checkIn?.days?.length ?? 0} day(s) configured)`}</div>
                          <IconButton
                            iconName="pencil"
                            iconStyle={IconStyle.SOLID}
                            theme={Theme.WHITE}
                            onClick={() => {
                              setEditingSubChallengeIndex(scIndex);
                              toggleEditModal(Sections.CheckIn);
                            }}
                          />
                        </div>

                        {/* Per-sub-challenge Front/Side Profile Image Section flag (MULTI mode).
                            Replaces the campaign-level toggle, which is hidden when MULTI. */}
                        <div className={styles['edit-user-form-container']}>
                          <div className={styles['input-container']}>
                            <div className={styles['label-text']}>Show Front/Side Profile Image Section</div>
                            <div className={styles['text-link-container']}>
                              <div className={styles['switch-container']}>
                                <Toggle
                                  toggleStyle={ToggleStyle.SWITCH}
                                  isActive={sc.campaignIsProfileImageSectionShown === 1}
                                  onClick={() => {
                                    handleInputChangeFlatten(
                                      `activities.subChallenges.${scIndex}.campaignIsProfileImageSectionShown`,
                                      sc.campaignIsProfileImageSectionShown ? 0 : 1,
                                    );
                                  }}
                                  theme={Theme.PURPLE}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className={styles['reward-header-label']}>{'Reward Configuration'}</div>
                        <div className={styles['edit-user-form-container']}>
                          <div className={styles['input-container']}>
                            <div className={styles['label-text']}>Show Reward Selection Form</div>
                            <div className={styles['text-link-container']}>
                              <div className={styles['switch-container']}>
                                <Toggle
                                  toggleStyle={ToggleStyle.SWITCH}
                                  isActive={sc.rewards?.showRewardSelectionForm === 1}
                                  onClick={() => {
                                    handleInputChangeFlatten(
                                      `activities.subChallenges.${scIndex}.rewards.showRewardSelectionForm`,
                                      sc.rewards?.showRewardSelectionForm ? 0 : 1,
                                    );
                                  }}
                                  disabled={
                                    !campaignData?.activities.participant.participationType ||
                                    campaignData?.activities.participant.participationType?.length === 0
                                  }
                                  theme={Theme.PURPLE}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className={styles['edit-user-form-container']}>
                          <div className={styles['input-container']}>
                            <DropDown
                              id={`sc-${scIndex}-rewardIndividual`}
                              key={`sc-${scIndex}-rewardIndividual`}
                              name={`sc-${scIndex}-rewardIndividual`}
                              dropdownLabel={'Reward Options - Individual Participants*'}
                              placeholder={'Reward Options - Individual Participants'}
                              mlpVersion={MLPVersion.ONE}
                              value={sc.rewards?.rewardOptions?.ind
                                ?.map(code => rewardList.find(r => r.id === code)?.name ?? null)
                                .filter((name): name is string => typeof name === 'string')}
                              data={rewardList}
                              multiSelection
                              allSelectionOption
                              configData={{
                                label: 'name',
                                onSelect: (selectedOptions: DropDownDataInterface[]) => {
                                  handleInputChangeFlatten(
                                    `activities.subChallenges.${scIndex}.rewards.rewardOptions.ind`,
                                    selectedOptions.map(option => option.id),
                                  );
                                },
                              }}
                              disabled={!campaignData?.activities.participant.participationType?.includes('ind')}
                              status={
                                campaignData?.activities.participant.participationType?.includes('ind') &&
                                sc.rewards?.showRewardSelectionForm === 1 &&
                                (!sc.rewards?.rewardOptions?.ind || sc.rewards?.rewardOptions?.ind?.length <= 0)
                                  ? DropDownValidations.INVALID
                                  : DropDownValidations.DEFAULT
                              }
                              message={
                                campaignData?.activities.participant.participationType?.includes('ind') &&
                                sc.rewards?.showRewardSelectionForm === 1 &&
                                (!sc.rewards?.rewardOptions?.ind || sc.rewards?.rewardOptions?.ind?.length <= 0)
                                  ? 'Please configure the reward options.'
                                  : ''
                              }
                            />
                          </div>
                          <div className={styles['input-container']}>
                            <DropDown
                              id={`sc-${scIndex}-rewardTeam`}
                              key={`sc-${scIndex}-rewardTeam`}
                              name={`sc-${scIndex}-rewardTeam`}
                              dropdownLabel={'Reward Options - Team Participants*'}
                              placeholder={'Reward Options - Team Participants'}
                              mlpVersion={MLPVersion.ONE}
                              value={sc.rewards?.rewardOptions?.team
                                ?.map(code => rewardList.find(r => r.id === code)?.name ?? null)
                                .filter((name): name is string => typeof name === 'string')}
                              data={rewardList}
                              multiSelection
                              allSelectionOption
                              configData={{
                                label: 'name',
                                onSelect: (selectedOptions: DropDownDataInterface[]) => {
                                  handleInputChangeFlatten(
                                    `activities.subChallenges.${scIndex}.rewards.rewardOptions.team`,
                                    selectedOptions.map(option => option.id),
                                  );
                                },
                              }}
                              disabled={!campaignData?.activities.participant.participationType?.includes('team')}
                              status={
                                campaignData?.activities.participant.participationType?.includes('team') &&
                                sc.rewards?.showRewardSelectionForm === 1 &&
                                (!sc.rewards?.rewardOptions?.team || sc.rewards?.rewardOptions?.team?.length <= 0)
                                  ? DropDownValidations.INVALID
                                  : DropDownValidations.DEFAULT
                              }
                              message={
                                campaignData?.activities.participant.participationType?.includes('team') &&
                                sc.rewards?.showRewardSelectionForm === 1 &&
                                (!sc.rewards?.rewardOptions?.team || sc.rewards?.rewardOptions?.team?.length <= 0)
                                  ? 'Please configure the reward options.'
                                  : ''
                              }
                            />
                          </div>
                        </div>
                      </Card>
                    ))}
                    <br />
                  </>
                )}

                {!usingSubChallenges && (
                  <>
                <div className={styles['reward-header-label']}>
                  <div>{t('admin.campaignConfig.orderRules')}</div>
                  <IconButton
                    iconName="pencil"
                    iconStyle={IconStyle.SOLID}
                    theme={Theme.WHITE}
                    onClick={() => toggleEditModal(Sections.OrderRule)}
                  />
                </div>
                {campaignData?.activities?.order?.rules.map((record, index) => (
                  <div className={styles['order-rules-container']}>
                    <Card key={`rule-${index + 1}`}>
                      <div className={styles['order-rules-container-headline']}>
                        <div>
                          {t('admin.campaignConfig.rule')} {index + 1}
                        </div>
                      </div>
                      {Array.isArray(record) &&
                        record.map((item, itemIndex) => (
                          <div className={styles['order-rules-container-grid-4-columns']}>
                            {itemIndex > 0 ? (
                              <DropDown
                                name={'rule-operator'}
                                dropdownLabel={`${t('admin.campaignConfig.operator')}*`}
                                placeholder={t('admin.campaignConfig.operator')}
                                value={ruleOperatorList.find(i => i.value === item.operator)?.title}
                                mlpVersion={MLPVersion.ONE}
                                data={ruleOperatorList}
                                configData={{
                                  label: 'title',
                                  onSelect: (selectedOption: DropDownDataInterface) => {
                                    handleInputChangeNormal(
                                      `activities.order.rules.${index}.${itemIndex}.operator`,
                                      selectedOption.value,
                                    );
                                  },
                                }}
                                disabled
                              />
                            ) : (
                              <div></div>
                            )}

                            <DropDown
                              name={'rule-type'}
                              dropdownLabel={`${t('admin.campaignConfig.type')}*`}
                              placeholder={t('admin.campaignConfig.type')}
                              value={ruleTypeList.find(i => i.value === item.type)?.title}
                              mlpVersion={MLPVersion.ONE}
                              data={ruleTypeList}
                              configData={{
                                label: 'title',
                                onSelect: (selectedOption: DropDownDataInterface) => {
                                  handleInputChangeNormal(
                                    `activities.order.rules.${index}.${index}.type`,
                                    selectedOption.value,
                                  );
                                },
                              }}
                              disabled
                            />

                            <Input
                              name={'rule-productSKU'}
                              value={Array.isArray(item.value) ? item.value.join(',') : item.value}
                              mlpVersion={MLPVersion.ONE}
                              label={t('admin.campaignConfig.productSKU')}
                              onChange={e => {
                                const inputValue = e.target.value;
                                const valuesArray = inputValue.split(',').map(item => item.trim());
                                handleInputChangeFlatten(`activities.order.rules.${index}.${index}.value`, valuesArray);
                              }}
                              disabled
                            />
                          </div>
                        ))}
                    </Card>
                  </div>
                ))}

                <br />

                <div className={styles['reward-header-label']}>
                  <div>{`Assessment Indicator Configuration`}</div>
                  <IconButton
                    iconName="pencil"
                    iconStyle={IconStyle.SOLID}
                    theme={Theme.WHITE}
                    onClick={() => toggleEditModal(Sections.WinningCriteria)}
                  />
                </div>
                <div className={styles['edit-user-form-container-three-columns']}>
                  {campaignData?.activities?.participant?.rules[4]?.subKpiCode
                    ?.filter(subKpi => subKpi.targetFrom !== null && subKpi.targetFrom !== '0')
                    ?.map(subKpi => (
                      <Input
                        type={InputTypes.NUMBER}
                        name={subKpi.subKpiCode}
                        label={`${subKpi.indicatorName} (${subKpi.indicatorMeasurementUnit})`}
                        value={subKpi.targetFrom}
                        mlpVersion={MLPVersion.ONE}
                        disabled={true}
                      />
                    ))}
                </div>
                  </>
                )}

                <br />

                {!usingSubChallenges && (
                  <>
                    <div className={styles['reward-header-label']}>
                      <div>Assessment File Upload Configuration</div>
                      <IconButton
                        iconName="pencil"
                        iconStyle={IconStyle.SOLID}
                        theme={Theme.WHITE}
                        onClick={() => toggleEditModal(Sections.FileUploader)}
                      />
                    </div>
                    <div className={styles['edit-user-form-container-three-columns']}>
                      {campaignData?.activities?.participant?.fileUploader
                        ?.filter(file => file.numberOfFile !== null && file.numberOfFile !== '0')
                        ?.map(file => (
                          <Input
                            type={InputTypes.NUMBER}
                            name={file.fieldName}
                            label={`${file.fieldName}`}
                            value={file.numberOfFile}
                            mlpVersion={MLPVersion.ONE}
                            disabled={true}
                          />
                        ))}
                    </div>
                  </>
                )}

                <br />
                <div className={styles['reward-header-label']}>
                  <div>{`Additional Settings`}</div>
                </div>
                {/* 3rd row period */}
                <div className={styles['edit-user-form-container']}>
                  <div className={styles['input-container']}>
                    <Input
                      type={InputTypes.NUMBER}
                      name={'hideSidebarDays'}
                      label={`Hide Challenge in Sidebar (Number of Days After End Date)`}
                      value={campaignData?.hideSidebarDays}
                      mlpVersion={MLPVersion.ONE}
                      onChange={e => {
                        handleInputChangeNormal('hideSidebarDays', e.target.value);
                        const dataRemovalDate = new Date(new Date(campaignData?.campaignEnd).getTime());
                        dataRemovalDate.setDate(
                          dataRemovalDate.getDate() + (180 + (e.target.value ? parseInt(e.target.value) : 0)),
                        );
                        handleInputChangeNormal('dataRemovalDate', dataRemovalDate);
                      }}
                      placeholder="e.g. 30"
                    />
                  </div>
                </div>
                {/* Reminder settings row: Pre-assessment reminder (banner + email only,
                    no form activation) on the left, post-assessment (activation + email)
                    on the right. */}
                <div className={styles['edit-user-form-container']}>
                  <div className={styles['input-container']}>
                    <Input
                      type={InputTypes.NUMBER}
                      name={'preAssessmentReminderDays'}
                      label={`Pre-assessment reminder notification (number of days after team start date)`}
                      value={campaignData?.preAssessmentReminderDays}
                      mlpVersion={MLPVersion.ONE}
                      onChange={e => {
                        handleInputChangeNormal('preAssessmentReminderDays', e.target.value);
                      }}
                      placeholder="e.g. 2"
                    />
                  </div>
                  <div className={styles['input-container']}>
                    <Input
                      type={InputTypes.NUMBER}
                      name={'postAssessmentReminderDays'}
                      label={`Activation of post-assessment section and notification (number of days before due date)`}
                      value={campaignData?.postAssessmentReminderDays}
                      mlpVersion={MLPVersion.ONE}
                      onChange={e => {
                        handleInputChangeNormal('postAssessmentReminderDays', e.target.value);
                      }}
                      placeholder="e.g. 7"
                    />
                  </div>
                </div>
                {/* 4rd row period */}
                <div className={styles['edit-user-form-container']}>
                  <div className={styles['input-container']}>
                    <div className={styles['label-text']}>Show Health Goal Section</div>
                    <div className={styles['text-link-container']}>
                      <div className={styles['switch-container']}>
                        <Toggle
                          toggleStyle={ToggleStyle.SWITCH}
                          disabled={false}
                          isActive={campaignData?.campaignIsHealthGoalSectionShown === 1}
                          onClick={() => {
                            setCampaignIsHealthGoalSectionShown(!campaignIsHealthGoalSectionShown);
                            handleInputChangeNormal(
                              'campaignIsHealthGoalSectionShown',
                              campaignIsHealthGoalSectionShown ? 0 : 1,
                            );
                          }}
                          theme={Theme.PURPLE}
                        />
                      </div>
                    </div>
                  </div>

                  <div className={styles['input-container']}>
                    <div className={styles['label-text']}>Show Progress Monitoring Section</div>
                    <div className={styles['text-link-container']}>
                      <div className={styles['switch-container']}>
                        <Toggle
                          toggleStyle={ToggleStyle.SWITCH}
                          disabled={false}
                          isActive={campaignData?.campaignIsProgressMonitoringSectionShown === 1}
                          onClick={() => {
                            setCampaignIsProgressMonitoringSectionShown(!campaignIsProgressMonitoringSectionShown);
                            handleInputChangeNormal(
                              'campaignIsProgressMonitoringSectionShown',
                              campaignIsProgressMonitoringSectionShown ? 0 : 1,
                            );
                          }}
                          theme={Theme.PURPLE}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* 5rd row period */}
                <div className={styles['edit-user-form-container']}>
                  {/* In MULTI mode this is configured per sub-challenge (see the
                      sub-challenge cards), so the campaign-level toggle is hidden. */}
                  {!usingSubChallenges && (
                    <div className={styles['input-container']}>
                      <div className={styles['label-text']}>Show Front/Side Profile Image Section</div>
                      <div className={styles['text-link-container']}>
                        <div className={styles['switch-container']}>
                          <Toggle
                            toggleStyle={ToggleStyle.SWITCH}
                            disabled={false}
                            isActive={campaignData?.campaignIsProfileImageSectionShown === 1}
                            onClick={() => {
                              setCampaignIsProfileImageSectionShown(!campaignIsProfileImageSectionShown);
                              handleInputChangeNormal(
                                'campaignIsProfileImageSectionShown',
                                campaignIsProfileImageSectionShown ? 0 : 1,
                              );
                            }}
                            theme={Theme.PURPLE}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                   <div className={styles['input-container']}>
                    <div className={styles['label-text']}>Enable Certificate</div>
                    <div className={styles['text-link-container']}>
                      <div className={styles['switch-container']}>
                        <Toggle
                          toggleStyle={ToggleStyle.SWITCH}
                          disabled={false}
                          isActive={campaignData?.certificateRequired === 1}
                          onClick={() => {
                            setCertificateRequired(!certificateRequired);
                            handleInputChangeNormal(
                              'certificateRequired',
                              certificateRequired ? 0 : 1,
                            );
                          }}
                          theme={Theme.PURPLE}
                        />
                      </div>
                    </div>
                  </div>
                </div>

              </>
            </Accordion>
            {/* participant section  */}
            <Accordion
              key={'participantAccordion'}
              id={'participantAccordion'}
              iconType={ControlIconType.CHEVRON}
              title={t('admin.campaignConfig.participantTitle')}
              childrenTitle={
                <Tag
                  theme={campaignData?.activities.participant.status === 'ACTIVE' ? Theme.SUCCESS : Theme.WARNING}
                  style={TagStyle.OUTLINE}
                  text={campaignData?.activities.participant.status}
                />
              }
            >
              <>
                <div className={styles['edit-user-form-container']}>
                  {/* name  */}
                  <div className={styles['input-container']}>
                    <Input
                      type={InputTypes.TEXT}
                      name={'name'}
                      placeholder={`${t('admin.campaignConfig.name')}`}
                      label={`${t('admin.campaignConfig.name')}`}
                      value={campaignData?.activities.participant.name}
                      mlpVersion={MLPVersion.ONE}
                      onChange={e => {
                        handleInputChangeNormal('activities.participant.name', e.target.value);
                      }}
                    />
                  </div>
                  {/* status */}
                  <div className={styles['input-container']}>
                    <div className={styles['label-text']}>{t('admin.campaignConfig.status')}</div>
                    <div className={styles['radio-button-container']}>
                      <RadioButton
                        label={t('admin.userEdit.active')}
                        checked={campaignData?.activities.participant.status === Status.ACTIVE}
                        onClick={() => {
                          handleInputChangeNormal('activities.participant.status', Status.ACTIVE);
                        }}
                      />
                      <RadioButton
                        label={t('admin.userEdit.inactive')}
                        checked={campaignData?.activities.participant.status === Status.INACTIVE}
                        onClick={() => {
                          handleInputChangeNormal('activities.participant.status', Status.INACTIVE);
                        }}
                      />
                    </div>
                  </div>
                  {/* description */}
                  <div className={styles['input-container']}>
                    <TextArea
                      mlpVersion={MLPVersion.ONE}
                      label={t('admin.campaignConfig.description')}
                      resize={false}
                      placeholder={t('admin.campaignConfig.description')}
                      value={campaignData?.activities.participant.description}
                      onChange={e => {
                        handleInputChangeNormal('activities.participant.description', e.target.value);
                      }}
                    />
                  </div>
                  {/* pin */}
                  <div className={styles['input-container']}>
                    <DropDown
                      id={'participantPin'}
                      key={'participantPin'}
                      name={'participantPin'}
                      dropdownLabel={t('admin.campaignConfig.pin')}
                      placeholder={t('admin.campaignConfig.pin')}
                      mlpVersion={MLPVersion.ONE}
                      data={pinIdSegmentList}
                      multiSelection
                      allSelectionOption
                      value={campaignData?.activities.participant.pin.map(pin => pin.pinName)}
                      configData={{
                        label: 'name',
                        onSelect: (selectedOptions: DropDownDataInterface[]) => {
                          const transformedOptions = selectedOptions.map(option => ({
                            pinId: option.id,
                            pinName: option.name,
                          }));
                          handleInputChangeNormal('activities.participant.pin', transformedOptions);
                        },
                      }}
                    />
                  </div>
                </div>
                <div className={styles['reward-header-label']}>{t('admin.campaignConfig.newAboConfiguration')}</div>
                <div className={styles['edit-user-form-container']}>
                  <div className={styles['text-link-container']}>
                    <div className={styles['switch-container']}>
                      <Toggle
                        toggleStyle={ToggleStyle.SWITCH}
                        disabled={false}
                        isActive={campaignData?.activities?.participant?.newAbo?.isRequired === 1}
                        onClick={() => {
                          // setToggleValue(!toggleValue);
                          handleInputChangeNormal(
                            'activities.participant.newAbo.isRequired',
                            campaignData?.activities?.participant?.newAbo?.isRequired ? 0 : 1,
                          );
                        }}
                        theme={Theme.PURPLE}
                      />
                    </div>
                    <Input
                      type={InputTypes.NUMBER}
                      name={'numOfNewAbo'}
                      label={`${t('admin.campaignConfig.numOfNewAbo')}`}
                      value={campaignData?.activities.participant?.newAbo?.numOfNewAbo}
                      mlpVersion={MLPVersion.ONE}
                      onChange={e => {
                        handleInputChangeNormal('activities.participant.newAbo.numOfNewAbo', e.target.value);
                      }}
                    />
                  </div>
                  <div className={styles['input-container']}>
                    <Input
                      type={InputTypes.NUMBER}
                      name={'numOfDays'}
                      label={`${t('admin.campaignConfig.numOfDays')}`}
                      value={campaignData?.activities.participant?.newAbo?.numOfDays}
                      mlpVersion={MLPVersion.ONE}
                      onChange={e => {
                        handleInputChangeNormal('activities.participant.newAbo.numOfDays', e.target.value);
                      }}
                    />
                  </div>
                </div>
              </>
            </Accordion>
            {/* coach section  */}
            <Accordion
              id={'coachAccordion'}
              key={'coachAccordion'}
              iconType={ControlIconType.CHEVRON}
              title={t('admin.campaignConfig.coachTitle')}
              childrenTitle={
                <Tag
                  theme={campaignData?.activities.coach.status === 'ACTIVE' ? Theme.SUCCESS : Theme.WARNING}
                  style={TagStyle.OUTLINE}
                  text={campaignData?.activities.coach.status}
                />
              }
            >
              <>
                <div className={styles['edit-user-form-container']}>
                  {/* name   */}
                  <div className={styles['input-container']}>
                    <Input
                      type={InputTypes.TEXT}
                      name={'name'}
                      label={`${t('admin.campaignConfig.name')}`}
                      placeholder={`${t('admin.campaignConfig.name')}`}
                      value={campaignData?.activities.coach.name}
                      mlpVersion={MLPVersion.ONE}
                      onChange={e => {
                        handleInputChangeNormal('activities.coach.name', e.target.value);
                      }}
                    />
                  </div>
                  {/* status  */}
                  <div className={styles['input-container']}>
                    <div className={styles['label-text']}>{t('admin.campaignConfig.campaignStatus')}</div>
                    <div className={styles['radio-button-container']}>
                      <RadioButton
                        label={t('admin.userEdit.active')}
                        checked={campaignData?.activities.coach.status === Status.ACTIVE}
                        onClick={() => {
                          handleInputChangeNormal('activities.coach.status', Status.ACTIVE);
                        }}
                      />
                      <RadioButton
                        label={t('admin.userEdit.inactive')}
                        checked={campaignData?.activities.coach.status === Status.INACTIVE}
                        onClick={() => {
                          handleInputChangeNormal('activities.coach.status', Status.INACTIVE);
                        }}
                      />
                    </div>
                  </div>
                  {/* description  */}
                  <div className={styles['input-container']}>
                    <TextArea
                      mlpVersion={MLPVersion.ONE}
                      resize={false}
                      label={t('admin.campaignConfig.description')}
                      placeholder={t('admin.campaignConfig.description')}
                      value={campaignData?.activities.coach.description}
                      onChange={e => {
                        handleInputChangeNormal('activities.coach.description', e.target.value);
                      }}
                    />
                  </div>
                  {/* pin  */}
                  <div className={styles['input-container']}>
                    <DropDown
                      id={'pinCoach'}
                      key={'pinCoach'}
                      name={'pin'}
                      dropdownLabel={t('admin.campaignConfig.pinCoach')}
                      placeholder={t('admin.campaignConfig.pinCoach')}
                      value={campaignData?.activities.coach.pin.map(pin => pin.pinName)}
                      mlpVersion={MLPVersion.ONE}
                      data={pinIdSegmentList}
                      multiSelection
                      allSelectionOption
                      configData={{
                        label: 'name',
                        onSelect: (selectedOptions: DropDownDataInterface[]) => {
                          const transformedOptions = selectedOptions.map(option => ({
                            pinId: option.id,
                            pinName: option.name,
                          }));
                          handleInputChangeNormal('activities.coach.pin', transformedOptions);
                        },
                      }}
                    />
                  </div>

                  <div className={styles['input-container']}>
                    <Input
                      type={InputTypes.NUMBER}
                      name={'teamLeaderReminderDays'}
                      label={`Team Leader Reminder (Number of Days After Team created)`}
                      value={campaignData?.activities.coach.teamLeaderReminderDays}
                      mlpVersion={MLPVersion.ONE}
                      onChange={e => {
                        handleInputChangeNormal('activities.coach.teamLeaderReminderDays', e.target.value);
                      }}
                      placeholder="e.g. 15"
                    />
                  </div>
                  <div className={styles['bottom-padding']}></div>
                </div>
              </>
            </Accordion>
            {showModal}
        </div>
      )}
      
      
    </div>
  );
};
